'use client';

/* eslint-disable consistent-return */
import React, { useEffect, useRef, useState } from 'react';
import { mergeProps, useFocus } from 'react-aria';
import { usePresenceAnimation } from 'components/Motion';
import { getShowingScrollbars } from 'components/ScrollContainer/ScrollContainer';
import styled, { css } from 'styled-components';
import { getBackgroundColor } from 'theme-utils';
import { isFunction } from 'utils/is-function';
import { mergeRefs } from '@react-aria/utils';
import type { ThemeColorsBackground } from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';
import { selectionStrategies } from './selection';
import type { AutoSelectOption } from './types';

const ANIMATION_DURATION = 150;
const RADIUS = theme.radii[4];

type FocusedBackgroundColor = Extract<
  ThemeColorsBackground,
  'primary' | 'transparent'
>;

export type InlineInputProps = {
  /**
   * Maximum number of rows to display when editing. This should either
   * be equal to or greater than lineClamp. If not included, editing
   * max row count will be equal to lineClamp (if included) or unlimited.
   */
  maxRows?: number;
  /**
   * Choose the auto select input behavior when entering edit mode, or provide
   * your own via a function.
   */
  autoSelect: AutoSelectOption;
  /** Background color when focused. Can be used to make the background
   * transparent when editing. */
  focusedBackgroundColor?: FocusedBackgroundColor;
  /** The padding of the input field */
  padding: string;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

function InlineInputComponent(
  {
    maxRows,
    autoSelect: autoSelectProp,
    value,
    padding,
    focusedBackgroundColor = 'primary',
    ...inputProps
  }: InlineInputProps,
  forwardedRef: any
) {
  const ref = useRef(null);
  const backgroundRef = useRef(null);
  const [hasFocus, setHasFocus] = useState(false);
  const { focusProps } = useFocus({ onFocusChange: setHasFocus });

  const autoSelect = useRef(autoSelectProp);
  autoSelect.current = autoSelectProp;

  usePresenceAnimation(backgroundRef, {
    enter: () => {
      // @ts-expect-error FIXME(strictNullChecks)
      const { width, height } = ref.current.getBoundingClientRect();
      const shadowOffset = 4;

      return {
        opacity: 0,
        scaleX: (width - shadowOffset) / width,
        scaleY: (height - shadowOffset) / height,
      };
    },
    target: {
      opacity: hasFocus ? 1 : 0,
      duration: ANIMATION_DURATION,
      scaleX: 1,
      scaleY: 1,
    },
    exit: () => {
      return {
        scaleX: 1,
        scaleY: 1,
        opacity: 0,
        duration: !value ? 0 : ANIMATION_DURATION,
      };
    },
  });

  useEffect(() => {
    if (autoSelect.current !== 'none') {
      // @ts-expect-error FIXME(strictNullChecks)
      ref.current.focus();

      const autoSelectFn = isFunction(autoSelect.current)
        ? autoSelect.current
        : selectionStrategies[autoSelect.current];

      if (autoSelectFn) {
        // @ts-expect-error FIXME(strictNullChecks)
        autoSelectFn(ref);
      }
    }
  }, []);

  const as = maxRows === 1 ? 'input' : 'textarea';
  const props = as === 'input' ? { size: 1 } : { rows: 1, cols: 1 };

  return (
    <>
      <StyledEditableField
        ref={mergeRefs(ref, forwardedRef)}
        $focusedBackgroundColor={focusedBackgroundColor}
        // @ts-expect-error FIXME(strictNullChecks)
        $maxRows={maxRows}
        $padding={padding}
        as={as}
        data-input
        value={value}
        {...(mergeProps(props, inputProps, focusProps) as object)}
      />
      <Background ref={backgroundRef} />
    </>
  );
}

function shadow(size: number) {
  return `0 0 0 ${size}px ${theme.colors.border.interactive.selected}`;
}

type StyledEditableFieldProps = {
  $maxRows: number;
  $focusedBackgroundColor: FocusedBackgroundColor;
  $padding: string;
};

const maxHeight = ({ $maxRows }: StyledEditableFieldProps) =>
  $maxRows ? `calc((var(--line-height) * ${$maxRows}))` : '';

const StyledEditableField = styled.textarea<StyledEditableFieldProps>`
  appearance: none;
  background: ${(p) => getBackgroundColor(p.$focusedBackgroundColor)};
  border: none;
  border-radius: calc(${RADIUS});
  box-shadow: 0 0 0 3px ${(p) => getBackgroundColor(p.$focusedBackgroundColor)};
  color: inherit;
  font: inherit;
  grid-area: 1 / 1 / 1 / 1;
  letter-spacing: inherit;
  line-height: var(--line-height);
  max-height: ${maxHeight};
  min-width: 1ch;
  outline: none;
  padding: ${(p) => p.$padding};
  resize: none;
  width: auto;
  z-index: ${theme.zIndices.overlay};
  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
  ${hideScrollbar};
`;

const Background = styled.div`
  border-radius: ${RADIUS};
  box-shadow: ${shadow(2)};
  content: '';
  grid-area: 1 / 1 / 1 / 1;
  margin: calc(${theme.spaces[2]} * -1);
  pointer-events: none;
  position: relative;
  transform-origin: center;
  z-index: calc(${theme.zIndices.overlay} + 1);
`;

function hideScrollbar() {
  if (getShowingScrollbars()) {
    return css`
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
    `;
  }
}

export const InlineInput = React.forwardRef(InlineInputComponent);
